/**
 * What the statistic panel shows for one number on the sheet: the value, the Override in force,
 * the breakdown (modifiers, dice, notes and the other roll extras), the Unsupported Rule Elements
 * that name its selectors, and which Override form fits (a modifier, a DC, a rank, a speed).
 */
import type { Override } from "../character";
import type { AbilityView, CheckExtrasView, DerivedSheet, HitPointsView, ModifierView, ProficiencyView, SpeedView, StatisticView, StrikeView, UnsupportedRule } from "../engine";
import { RANK_LABELS, signed } from "../engine";

export type OverrideForm = "modifier" | "dc" | "rank" | "speed" | "none";

export interface StatDetail {
  /** The Override key; also the panel's identity. */
  key: string;
  label: string;
  form: OverrideForm;
  /** What the sheet shows, formatted. */
  shown: string;
  /** The engine's value, formatted, when an Override replaces it. */
  engine: string | null;
  override: Override | null;
  /** The numeric value in force (for the Override form's initial value and the roll outlook). */
  value: number;
  /** A first line before the modifiers ("10", "25 ft", the base ancestry HP). */
  base: string | null;
  modifiers: ModifierView[];
  dice: string[];
  extras: CheckExtrasView | null;
  unsupported: UnsupportedRule[];
  /** What granted the modifier's source Entry, by source UUID. */
  grantedBy: Map<string, string | null>;
  /** The engine's own one-line breakdown, shown when there are no modifiers to list. */
  summary: string | null;
}

/** Unsupported Rule Elements naming any of the selectors (a `selector` may list several, comma-separated). */
export function unsupportedFor(sheet: Pick<DerivedSheet, "unsupported">, selectors: string[]): UnsupportedRule[] {
  const wanted = new Set(selectors);
  return sheet.unsupported.filter((u) => u.selector?.split(", ").some((s) => wanted.has(s)));
}

export function grantersOf(sheet: Pick<DerivedSheet, "items">): Map<string, string | null> {
  return new Map(sheet.items.map((i) => [i.uuid, i.grantedBy?.name ?? null]));
}

type Ctx = { sheet: Pick<DerivedSheet, "unsupported" | "items">; grantedBy?: Map<string, string | null> };

function granters(ctx: Ctx): Map<string, string | null> {
  return ctx.grantedBy ?? grantersOf(ctx.sheet);
}

/** A check statistic (a save, Perception, a skill, a class DC, a special statistic) as a modifier or, for DCs, its DC. */
export function statisticDetail(view: StatisticView, ctx: Ctx, options: { dc?: boolean; selectors?: string[]; label?: string } = {}): StatDetail {
  const dc = options.dc === true;
  const selectors = options.selectors ?? [view.slug];
  return {
    key: view.overrideKey,
    label: options.label ?? view.label,
    form: dc ? "dc" : "modifier",
    shown: dc ? String(view.value) : signed(view.value),
    engine: view.override ? (dc ? String(view.derived) : signed(view.derived)) : null,
    override: view.override,
    value: view.value,
    base: dc ? "10" : null,
    modifiers: dc ? view.dcModifiers : view.modifiers,
    dice: [],
    extras: view,
    unsupported: unsupportedFor(ctx.sheet, [...selectors, "all"]),
    grantedBy: granters(ctx),
    summary: dc ? view.dcBreakdown : view.breakdown,
  };
}

export function hitPointsDetail(view: HitPointsView, ctx: Ctx, label = "Hit Points"): StatDetail {
  return {
    key: view.overrideKey,
    label,
    form: "dc",
    shown: String(view.value),
    engine: view.override ? String(view.derived) : null,
    override: view.override,
    value: view.value,
    base: null,
    modifiers: view.modifiers,
    dice: [],
    extras: null,
    unsupported: unsupportedFor(ctx.sheet, ["hp", "hp-per-level", "all"]),
    grantedBy: granters(ctx),
    summary: view.breakdown,
  };
}

export function speedDetail(view: SpeedView, ctx: Ctx): StatDetail {
  return {
    key: view.overrideKey,
    label: view.label,
    form: "speed",
    shown: `${view.value} ft`,
    engine: view.override ? `${view.derived} ft` : null,
    override: view.override,
    value: view.value,
    base: view.breakdown.split(", ")[0] ?? null,
    modifiers: view.modifiers,
    dice: [],
    extras: null,
    unsupported: unsupportedFor(ctx.sheet, [`${view.type}-speed`, "speed", "all-speeds", "all"]),
    grantedBy: granters(ctx),
    summary: view.breakdown,
  };
}

export function abilityDetail(view: AbilityView, ctx: Ctx): StatDetail {
  return {
    key: view.overrideKey,
    label: view.label,
    form: "modifier",
    shown: signed(view.value),
    engine: view.override ? signed(view.derived) : null,
    override: view.override,
    value: view.value,
    base: `Base ${signed(view.base)} from boosts and flaws`,
    modifiers: [],
    dice: [],
    extras: null,
    unsupported: unsupportedFor(ctx.sheet, [`${view.attribute}-based`, "all"]),
    grantedBy: granters(ctx),
    summary: view.derived !== view.base ? `Adjusted by Entries to ${signed(view.derived)}` : null,
  };
}

export function proficiencyDetail(view: ProficiencyView, ctx: Ctx): StatDetail {
  return {
    key: view.overrideKey,
    label: view.label,
    form: "rank",
    shown: `${RANK_LABELS[view.rank]} (${signed(view.value)})`,
    engine: view.override ? RANK_LABELS[view.derived] : null,
    override: view.override,
    value: view.rank,
    base: view.sourceName ? `From ${view.sourceName}` : null,
    modifiers: [],
    dice: [],
    extras: null,
    unsupported: unsupportedFor(ctx.sheet, [view.slug, `${view.slug}-attack`, "all"]),
    grantedBy: granters(ctx),
    summary: view.breakdown,
  };
}

export function strikeDetail(view: StrikeView, ctx: Ctx): StatDetail {
  return {
    key: view.overrideKey,
    label: `${view.label} attack${view.thrown ? " (thrown)" : ""}`,
    form: "modifier",
    shown: signed(view.value),
    engine: view.override ? signed(view.derived) : null,
    override: view.override,
    value: view.value,
    base: null,
    modifiers: view.modifiers,
    dice: [],
    extras: view,
    unsupported: unsupportedFor(ctx.sheet, ["attack", "attack-roll", "strike-attack-roll", `${view.slug}-attack`, "all"]),
    grantedBy: granters(ctx),
    summary: view.breakdown,
  };
}

export function strikeDamageDetail(view: StrikeView, ctx: Ctx): StatDetail | null {
  const damage = view.damage;
  if (!damage) return null;
  return {
    key: `${view.overrideKey}:damage`,
    label: `${view.label} damage`,
    form: "none",
    shown: damage.breakdown.success.join(", ") || (damage.formula.success ?? "—"),
    engine: null,
    override: null,
    value: 0,
    base: damage.breakdown.success[0] ?? null,
    modifiers: damage.modifiers,
    dice: damage.dice.map((d) => `${d.label}: ${d.diceNumber}${d.dieSize ?? ""}${d.damageType ? ` ${d.damageType}` : ""}${d.critical === true ? " (critical only)" : ""}${d.enabled ? "" : " · off"}`),
    extras: { notes: damage.notes, degreeAdjustments: [], rollTwice: false, rollTwiceSources: [], substitutions: [], ephemeralEffects: damage.ephemeralEffects },
    unsupported: unsupportedFor(ctx.sheet, ["damage", "strike-damage", `${view.slug}-damage`, "all"]),
    grantedBy: granters(ctx),
    summary: damage.formula.criticalSuccess ? `Critical: ${damage.breakdown.criticalSuccess.join(", ") || damage.formula.criticalSuccess}` : null,
  };
}

/** The Overrides list with one key replaced, set or cleared. */
export function withOverride(overrides: Override[], key: string, value: number | null, note: string): Override[] {
  const others = overrides.filter((o) => o.statistic !== key);
  if (value === null) return others;
  const trimmed = note.trim();
  return [...others, trimmed ? { statistic: key, value, note: trimmed } : { statistic: key, value }];
}
